import type { HarnessId } from "../providerEvents/harnessEventTypes"
import { isHarnessId } from "../modelRegistry"

export const NEW_TASK_PINNED_AGENT_LIMIT = 5

const MAX_AGENT_TARGET_ID_LENGTH = 200

import { agentTargetId, isThinkingLevel, type ThinkingLevel } from "../agentTarget"

export interface NewTaskAgentTarget {
    thinking?: ThinkingLevel
    harnessId: HarnessId
    modelId: string
}

function isTargetId(value: unknown): value is string {
    return typeof value === "string" && value.length > 0 && value.length <= MAX_AGENT_TARGET_ID_LENGTH
}

function isNewTaskAgentTarget(value: unknown): value is NewTaskAgentTarget {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return false
    return (
        (!("thinking" in value) || value.thinking === undefined || isThinkingLevel(value.thinking)) &&
        "harnessId" in value &&
        "modelId" in value &&
        isHarnessId(value.harnessId) &&
        isTargetId(value.modelId)
    )
}

function targetId(target: NewTaskAgentTarget): string {
    return agentTargetId(target)
}

export function normalizeNewTaskPinnedAgents(value: unknown): NewTaskAgentTarget[] {
    if (!Array.isArray(value)) return []
    const seen = new Set<string>()
    const normalized: NewTaskAgentTarget[] = []
    for (const entry of value) {
        if (!isNewTaskAgentTarget(entry)) continue
        const id = targetId(entry)
        if (seen.has(id)) continue
        normalized.push({ harnessId: entry.harnessId, modelId: entry.modelId, ...(entry.thinking ? { thinking: entry.thinking } : {}) })
        seen.add(id)
        if (normalized.length === NEW_TASK_PINNED_AGENT_LIMIT) break
    }
    return normalized
}

export function toggleNewTaskAgentPin(pinned: readonly NewTaskAgentTarget[], target: NewTaskAgentTarget): NewTaskAgentTarget[] {
    const normalized = normalizeNewTaskPinnedAgents(pinned)
    const id = targetId(target)
    const existingIndex = normalized.findIndex((entry) => targetId(entry) === id)
    if (existingIndex >= 0) return normalized.filter((_, index) => index !== existingIndex)
    if (normalized.length >= NEW_TASK_PINNED_AGENT_LIMIT) return normalized
    return [...normalized, { harnessId: target.harnessId, modelId: target.modelId, ...(target.thinking ? { thinking: target.thinking } : {}) }]
}
